import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLivePricing, livePrice } from '../../utils/pricingApi';

const { FiZap, FiArrowRight } = FiIcons;

/* Live price or graceful placeholder — never a hardcoded/invented number.
 * Until the manuscriptsmith Chargebee item family exists, this shows a subtle
 * "soon" placeholder instead of a fake price (see src/utils/pricingApi.js). */
const LivePrice = ({ state, plan, isFree, light }) => {
  const price = livePrice(state, plan, { isFree });
  if (price) return <>{price}</>;
  return (
    <span className={`text-2xl align-middle ${light ? 'opacity-80' : 'text-gray-400'}`} title="Live pricing from Chargebee">
      {state.status === 'loading' ? '···' : 'Soon'}
    </span>
  );
};

const Pricing = () => {
  const navigate = useNavigate();
  const pricing = useLivePricing();

  const scrollToForm = () => {
    const formElement = document.getElementById('transform-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleViewFullPricing = () => {
    navigate('/pricing');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  return (
    <section id="pricing" className="py-24 bg-gradient-to-br from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Simple, <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">Transparent</span> Pricing
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Choose the plan that fits your publishing needs.
          </motion.p>
        </div>

        {/* Pricing Preview Cards */}
        {/* price TBD (research job running): entry plan removed; paid plans only */}
        <div className="grid md:grid-cols-2 gap-8 mb-12 max-w-4xl mx-auto">
          {/* Professional - Most Popular */}
          <motion.div 
            className="bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl p-8 shadow-xl text-white relative transform scale-105"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Floating Badge - Over the corner */}
            <div className="absolute -top-3 right-4 bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full shadow-lg z-20 uppercase tracking-wide">
              Most Popular
            </div>
            
            <h3 className="text-2xl font-bold mb-2">Author</h3>
            <div className="text-4xl font-bold mb-2"><LivePrice state={pricing} plan="author" light /></div>
            <p className="opacity-90 mb-6">per month</p>
            <ul className="space-y-3 mb-8">
              <li>Unlimited books &amp; words</li>
              <li>Full export (EPUB, PDF, DOCX, KDP)</li>
              <li>Live KDP Preflight</li>
              <li>Author Voice DNA (up to 3 books)</li>
            </ul>
            <button 
              onClick={handleViewFullPricing}
              className="w-full py-3 bg-white text-primary-600 font-bold rounded-lg hover:bg-gray-50 transition-colors"
            >
              View Details
            </button>
          </motion.div>

          {/* Enterprise */}
          <motion.div 
            className="bg-gray-900 rounded-2xl p-8 shadow-xl text-white border-2 border-yellow-400"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h3 className="text-2xl font-bold mb-2 flex items-center">
              <SafeIcon icon={FiZap} className="w-5 h-5 mr-2 text-yellow-400" />
              Pro
            </h3>
            <div className="text-4xl font-bold mb-2"><LivePrice state={pricing} plan="pro" /></div>
            <p className="text-gray-300 mb-6">per month</p>
            <ul className="space-y-3 mb-8">
              <li>Everything in Author</li>
              <li>Advanced AI &amp; collaboration</li>
              <li>Audiobook add-on</li>
              <li>API access &amp; priority support</li>
            </ul>
            <button 
              onClick={handleViewFullPricing}
              className="w-full py-3 bg-yellow-400 text-gray-900 font-bold rounded-lg hover:bg-yellow-300 transition-colors"
            >
              Learn More
            </button>
          </motion.div>
        </div>

        {/* View Full Pricing CTA */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <button 
            onClick={handleViewFullPricing}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600 transition-all transform hover:scale-105 shadow-lg"
          >
            View Complete Pricing & Comparison <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;